import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { i18n } from '@/utils/i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Input } from "@vaiu/ui/input";
import { Label } from "@vaiu/ui/label";
import { Badge } from "@vaiu/ui/badge";
import { Separator } from "@vaiu/ui/separator";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@vaiu/ui/select';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { 
  ArrowLeft,
  Wallet,
  Plus,
  DollarSign,
  AlertCircle,
  TrendingUp,
  Zap,
  Target
} from 'lucide-react';
import { toast } from "sonner";
import { CurrencyDisplay } from '@vaiu/ui';
import { platformApi } from '@/services/platformApi';
import StripeProvider from '@/components/payment/StripeProvider';
import TopUpBalance from '@/components/TopUpBalance';

/**
 * TopupPage - Top-up page using TopUpBalance component
 * 
 * Benefits:
 * - Apple Pay/Google Pay automatically available
 * - Consistent payment UI across the app
 * - ATM withdrawal option available
 * - All payment methods in one place
 */
const TopupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // State management
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const [topUpAmount, setTopUpAmount] = useState<number>(10);
  const [allBalances, setAllBalances] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showPaymentFlow, setShowPaymentFlow] = useState(false);
  const [acceptableCurrencies, setAcceptableCurrencies] = useState<string[]>(['USD', 'EUR', 'GBP']);

  // Quick amount options
  const quickAmounts = [10, 25, 50];

  // Get current balance for selected currency
  // Ensure currency key matches (uppercase)
  const currentBalance = allBalances[selectedCurrency.toUpperCase()] || allBalances[selectedCurrency] || 0;
  
  // Debug logging for balance display
  useEffect(() => {
    console.log('[TopupPage] Balance display debug:', {
      selectedCurrency,
      allBalances,
      balanceKeys: Object.keys(allBalances),
      currentBalance,
      balanceForSelectedCurrency: allBalances[selectedCurrency],
      balanceForUppercase: allBalances[selectedCurrency.toUpperCase()]
    });
  }, [selectedCurrency, allBalances, currentBalance]);

  // Initialize balances from user object when component mounts or user changes
  useEffect(() => {
    if (user?.balances && Object.keys(user.balances).length > 0) {
      console.log('🔄 Initializing balances from user object:', user.balances);
      
      // Normalize currency keys to uppercase for consistency
      const normalizedUserBalances: Record<string, number> = {};
      Object.keys(user.balances).forEach(key => {
        normalizedUserBalances[key.toUpperCase()] = user.balances[key];
      });
      
      console.log('🔄 Normalized user balances:', normalizedUserBalances);
      setAllBalances(normalizedUserBalances);
    }
  }, [user]);

  // Function to build full return URL (origin + port + path + query params)
  const buildFullReturnUrl = useCallback(() => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    const pathname = window.location.pathname;
    const search = window.location.search;
    
    // For localhost, always use http and explicitly include port
    let origin: string;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const correctedProtocol = 'http:';
      if (port) {
        origin = `${correctedProtocol}//${hostname}:${port}`;
      } else {
        origin = `${correctedProtocol}//${hostname}`;
      }
    } else {
      // For production, use the original protocol and include port if it exists
      if (port) {
        origin = `${protocol}//${hostname}:${port}`;
      } else {
        origin = `${protocol}//${hostname}`;
      }
    }
    
    return `${origin}${pathname}${search}`;
  }, []);

  // Define loadTopUpData BEFORE useEffect that uses it
  const loadTopUpData = async () => {
    try {
      setLoading(true);
      
      // Debug: Log current state
      console.log('🔍 TopupPage loadTopUpData - user:', {
        hasUser: !!user,
        hasBalances: !!(user?.balances),
        balances: user?.balances,
        balancesKeys: user?.balances ? Object.keys(user.balances) : [],
        balancesCount: user?.balances ? Object.keys(user.balances).length : 0,
        currentAllBalances: allBalances
      });
      
      // Preserve existing balances from user object (don't start with empty object)
      // Normalize currency keys to uppercase
      let balances: Record<string, number> = {};
      if (user?.balances) {
        Object.keys(user.balances).forEach(key => {
          balances[key.toUpperCase()] = user.balances[key];
        });
      }
      
      // Try API to get the latest balances, but don't overwrite with empty results
      try {
        // Uses /api/v1/profile?fields=balances endpoint
        // Returns {balances: {USD: 2.09, EUR: 50.00, ...}}
        const apiBalances = await platformApi.getBalances();
        console.log('✅ Loaded balances from API:', apiBalances);
        
        // Only use API balances if they actually have values
        // Don't overwrite user.balances with empty API response
        if (apiBalances && typeof apiBalances === 'object' && Object.keys(apiBalances).length > 0) {
          // Check if API balances have any non-zero values
          const hasNonZeroBalances = Object.values(apiBalances).some(val => val > 0);
          if (hasNonZeroBalances || Object.keys(apiBalances).length > 0) {
            console.log('✅ Using API balances (has values):', apiBalances);
            balances = apiBalances;
          } else {
            console.log('⚠️ API returned zero balances, keeping user.balances:', balances);
          }
        } else {
          // API returned empty object - keep the user.balances we already have
          console.log('⚠️ API returned empty object, keeping user.balances:', balances);
        }
      } catch (apiError) {
        console.error('❌ Failed to load balances from API:', apiError);
        // Keep existing balances from user object if API fails
        if (Object.keys(balances).length > 0) {
          console.log('✅ Keeping user.balances since API failed:', balances);
        } else {
          console.warn('⚠️ No balances available from user object or API');
        }
        // Don't throw error - we can still use user.balances
      }
      
      console.log('💰 Final balances to set:', balances);
      console.log('💰 Balance keys:', Object.keys(balances));
      console.log('💰 Balance values:', Object.values(balances));
      
      // Normalize currency keys to uppercase to ensure consistency
      const normalizedBalances: Record<string, number> = {};
      Object.keys(balances).forEach(key => {
        normalizedBalances[key.toUpperCase()] = balances[key];
      });
      
      console.log('💰 Normalized balances:', normalizedBalances);
      
      // Only update if we have balances to avoid overwriting with empty object
      if (Object.keys(normalizedBalances).length > 0) {
        setAllBalances(normalizedBalances);
      } else {
      console.warn('⚠️ Not setting empty balances, keeping current state');
      }
      
      // Load acceptable currencies
      const currencies = await platformApi.getAcceptableCurrencies();
      setAcceptableCurrencies(currencies.all || ['USD', 'EUR', 'GBP']);
      
      // Set user's preferred currency
      const requestedCurrency = searchParams.get('currency')?.toUpperCase();
      if (requestedCurrency && currencies.all.includes(requestedCurrency)) {
        setSelectedCurrency(requestedCurrency);
      } else if (user?.currency && currencies.all.includes(user.currency)) {
        setSelectedCurrency(user.currency);
      }
    } catch (error) {
      console.error('Failed to load top-up data:', error);
      // Don't show error toast if we have balances from user object
      if (!user?.balances || Object.keys(user.balances).length === 0) {
        toast.error('Failed to load balance information');
      }
    } finally {
      setLoading(false);
    }
  };

  // useEffect AFTER loadTopUpData is defined
  useEffect(() => {
    loadTopUpData();
  }, [user, searchParams]); // Re-load when user changes or a currency hint is provided

  // Handle PayPal return from redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paypalStatus = searchParams.get('paypal');
    const paymentId = searchParams.get('paymentId');
    
    if (paypalStatus === 'complete') {
      console.log('✅ PayPal payment completed, refreshing balances...');
      
      // Refresh balances to show updated amount
      loadTopUpData().then(() => {
        toast.success('PayPal payment completed successfully!');
        
        // Clean up URL parameters
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }).catch((error) => {
        console.error('Failed to refresh balances after PayPal payment:', error);
        toast.error('Payment completed but failed to refresh balance. Please refresh the page.');
      });
    } else if (paypalStatus === 'cancelled') {
      toast.info('PayPal payment was cancelled');
      
      // Clean up URL parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    } else if (paypalStatus === 'error') {
      const errorMsg = searchParams.get('error');
      toast.error(errorMsg || 'PayPal payment failed');
      
      // Clean up URL parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePaymentSuccess = async (newBalance: number, fundingDetails?: any) => {
    try {
      // Refresh all balances from API to ensure accuracy
      const balances = await platformApi.getBalances();
      setAllBalances(balances);
      
      // Payment already processed by TopUpBalance
      toast.success(`Successfully added ${topUpAmount} ${selectedCurrency} to your balance!`);
      
      // Reset form
      setShowPaymentFlow(false);
      setTopUpAmount(10);
      
      // Navigate to dashboard after short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      console.error('Error in handlePaymentSuccess:', error);
      toast.error(error.message || 'Failed to complete top-up');
    }
  };

  const handleQuickAmount = (amount: number) => {
    setTopUpAmount(amount);
    setShowPaymentFlow(true);
  };

  const handleCustomAmount = () => {
    if (topUpAmount > 0) {
      setShowPaymentFlow(true);
    } else {
      toast.error('Please enter a valid amount');
    }
  };

  return (
    <div className="min-h-screen w-full max-w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto min-w-0 px-2 py-8 sm:px-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.__('topup.back')}
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{i18n.__('topup.title')}</h1>
              <p className="text-muted-foreground mt-1">
                {i18n.__('topup.subtitle')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">{i18n.__('topup.currentBalance')}</p>
              <p className="text-2xl font-bold">
                <CurrencyDisplay amount={currentBalance} currency={selectedCurrency} currencyService={currencyService} />
              </p>
            </div>
          </div>
        </div>

        {!showPaymentFlow ? (
          <div className="max-w-4xl mx-auto grid gap-6 md:grid-cols-2">
            {/* Amount Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  {i18n.__('topup.selectAmount')}
                </CardTitle>
                <CardDescription>
                  {i18n.__('topup.selectAmountDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Currency Selection */}
                <div>
                  <Label htmlFor="currency">{i18n.__('topup.currency')}</Label>
                  <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                    <SelectTrigger id="currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {acceptableCurrencies.map(curr => (
                        <SelectItem key={curr} value={curr}>
                          {curr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quick Amounts */}
                <div>
                  <Label>{i18n.__('topup.quickSelect')}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {quickAmounts.map(amount => (
                      <Button
                        key={amount}
                        variant={topUpAmount === amount ? 'default' : 'outline'}
                        onClick={() => setTopUpAmount(amount)}
                        className="font-semibold"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Custom Amount */}
                <div>
                  <Label htmlFor="custom-amount">{i18n.__('topup.customAmount')}</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="custom-amount"
                      type="number"
                      min="1"
                      step="0.01"
                      value={topUpAmount}
                      onChange={(e) => setTopUpAmount(parseFloat(e.target.value) || 0)}
                      placeholder={i18n.__('topup.enterAmount')}
                    />
                    <Button 
                      onClick={handleCustomAmount}
                      className="px-8"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {i18n.__('topup.addFunds')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Benefits Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  {i18n.__('topup.whyAddFunds')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Wallet className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{i18n.__('topup.instantCreation')}</p>
                      <p className="text-sm text-muted-foreground">
                        {i18n.__('topup.instantCreationDesc')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{i18n.__('topup.betterRates')}</p>
                      <p className="text-sm text-muted-foreground">
                        {i18n.__('topup.betterRatesDesc')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Target className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">{i18n.__('topup.multipleCurrencies')}</p>
                      <p className="text-sm text-muted-foreground">
                        {i18n.__('topup.multipleCurrenciesDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{i18n.__('topup.securePayment')}</strong> {i18n.__('topup.securePaymentDesc')}
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        ) : (
          <StripeProvider>
            <div className="mx-auto w-full max-w-2xl min-w-0">
              <TopUpBalance
                currentBalance={currentBalance}
                requiredAmount={topUpAmount}
                currency={selectedCurrency}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setShowPaymentFlow(false)}
                isOpen={showPaymentFlow}
                context={{
                  type: 'profile',
                  returnUrl: buildFullReturnUrl()
                }}
              />
            </div>
          </StripeProvider>
        )}
      </div>
    </div>
  );
};

export default TopupPage;
